import React, { useState, useEffect } from 'react'
import { LocaleCode } from '../types/component'
import { usePage } from '../hooks/usePage'
import { ComponentRenderer } from './ComponentRenderer'
import { ToastContainer } from './ui/ToastContainer'

export interface PageRendererProps {
  /**
   * The BigCommerce store ID
   */
  storeId: string

  /**
   * The page slug to load
   */
  pageSlug: string

  /**
   * The API URL for the backend
   * @default 'http://localhost:3000'
   */
  apiUrl?: string

  /**
   * The locale for localized content
   * @default 'en'
   */
  locale?: LocaleCode

  /**
   * BigCommerce cart ID for cart operations
   */
  cartId?: string | null

  /**
   * BigCommerce wishlist ID for wishlist operations
   */
  wishlistId?: string | null

  /**
   * Callback when cart is created or updated
   */
  onCartUpdate?: (cartId: string) => void

  /**
   * Callback when wishlist is created or updated
   */
  onWishlistUpdate?: (wishlistId: string) => void

  /**
   * Custom loading component
   */
  loading?: React.ReactNode

  /**
   * Custom error component
   */
  error?: (error: Error) => React.ReactNode

  /**
   * Optional CSS class name for the root container
   */
  className?: string

  /**
   * Optional inline styles for the root container
   */
  style?: React.CSSProperties
}

/**
 * Main page renderer component
 *
 * @example
 * ```tsx
 * import { PageRenderer } from '@bc-editor/renderer'
 *
 * function MyPage() {
 *   return (
 *     <PageRenderer
 *       storeId="abc123"
 *       pageSlug="homepage"
 *       apiUrl="https://api.example.com"
 *     />
 *   )
 * }
 * ```
 */
export function PageRenderer({
  storeId,
  pageSlug,
  apiUrl = 'http://localhost:3000',
  locale = 'en',
  cartId: externalCartId,
  wishlistId: externalWishlistId,
  onCartUpdate,
  onWishlistUpdate,
  loading,
  error: errorComponent,
  className,
  style,
}: PageRendererProps) {
  const { page, isLoading, error } = usePage({
    apiUrl,
    storeId,
    pageSlug,
    locale,
  })

  // Internal cart state management (persisted in localStorage)
  const [internalCartId, setInternalCartId] = useState<string | null>(null)
  const [internalWishlistId, setInternalWishlistId] = useState<string | null>(null)

  // Load cart/wishlist from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedCartId = localStorage.getItem(`bc_cart_${storeId}`)
      const savedWishlistId = localStorage.getItem(`bc_wishlist_${storeId}`)
      if (savedCartId) setInternalCartId(savedCartId)
      if (savedWishlistId) setInternalWishlistId(savedWishlistId)
    }
  }, [storeId])

  // Handle cart updates
  const handleCartUpdate = (cartId: string) => {
    setInternalCartId(cartId)
    if (typeof window !== 'undefined') {
      localStorage.setItem(`bc_cart_${storeId}`, cartId)
    }
    onCartUpdate?.(cartId)
  }

  // Handle wishlist updates
  const handleWishlistUpdate = (wishlistId: string) => {
    setInternalWishlistId(wishlistId)
    if (typeof window !== 'undefined') {
      localStorage.setItem(`bc_wishlist_${storeId}`, wishlistId)
    }
    onWishlistUpdate?.(wishlistId)
  }

  // Use external cart/wishlist if provided, otherwise use internal state
  const cartId = externalCartId !== undefined ? externalCartId : internalCartId
  const wishlistId = externalWishlistId !== undefined ? externalWishlistId : internalWishlistId

  if (isLoading) {
    return (
      <>
        <div className={className} style={style}>
          {loading || (
            <div className="py-10 text-center text-muted-foreground">
              <p>Loading page...</p>
            </div>
          )}
        </div>
        <ToastContainer />
      </>
    )
  }

  if (error) {
    return (
      <>
        <div className={className} style={style}>
          {errorComponent ? (
            errorComponent(error)
          ) : (
            <div className="py-10 text-center text-destructive">
              <h2 className="text-xl font-bold mb-2">Error loading page</h2>
              <p>{error.message}</p>
            </div>
          )}
        </div>
        <ToastContainer />
      </>
    )
  }

  if (!page) {
    return (
      <>
        <div className={className} style={style}>
          <div className="py-10 text-center text-muted-foreground">
            <p>Page not found</p>
          </div>
        </div>
        <ToastContainer />
      </>
    )
  }

  return (
    <>
      <div className={className} style={style}>
        {page.components.map((component) => (
          <ComponentRenderer
            key={component.id}
            component={component}
            locale={locale}
            storeId={storeId}
            apiUrl={apiUrl}
            cartId={cartId}
            wishlistId={wishlistId}
            onCartUpdate={handleCartUpdate}
            onWishlistUpdate={handleWishlistUpdate}
          />
        ))}
      </div>
      <ToastContainer />
    </>
  )
}
